import { useContext, useEffect, useRef, useState } from "react";
import ValueContext from "../../ContextAPi/appContext";

/**
 * =============================================================================
 * Editor Auto-Save
 * =============================================================================
 *
 * PURPOSE
 * -------
 * Silently persist canvas edits so users do not lose work if they forget
 * manual Save. This is intentionally a *lightweight* save path — not a full
 * clone of the header Save button.
 *
 * WHAT IT SAVES (slim path)
 * ------------------------
 * - Canvas layers + animation via `persistBannerCanvas` → `saveAssets`
 *   → PUT `api/v1/assets/{id}/assetpages/assetpagecomponent`
 * - `origin: "autoSave"` suppresses success/error toasts (same idea as
 *   "hitAreaSave" / "change").
 *
 * WHAT IT DOES NOT SAVE (by design — keep auto-save cheap)
 * --------------------------------------------------------
 * - Metadata PATCH (`patchCall` / brand, name, fps, etc.)
 * - File-size validation, RSD screenshot, thumbnail upload
 * - Those remain on the manual Save flow in `EditorHeaderForTemplate`.
 *
 * WHY NOT GATE ONLY ON `bannerChanged`?
 * -------------------------------------
 * Dirty flags in this editor are incomplete: many edits update `layerCompData`
 * or mutate `tempLayer` in place without reliably flipping `bannerChanged`.
 * Auto-save therefore treats content as dirty when either:
 *   1) `layerCompData` / `animationObject` revision advanced, OR
 *   2) `bannerChanged` / `refBannerChanged` is true (DOM mutation observer).
 *
 * SCHEDULING MODEL (debounce + max-wait)
 * --------------------------------------
 * - Debounce (4s): save shortly after the user *stops* editing.
 * - Max-wait (30s): if they keep editing without pause, force a save so work
 *   is not held forever. Max-wait arms once per dirty streak; debounce resets
 *   on every change.
 *
 * LIFECYCLE / SAFETY
 * ------------------
 * 1) Wait until `refreshCompleted` (asset GET finished) + short settle delay
 *    before enabling — avoids PUT of empty/partial canvas during hydration.
 * 2) Snapshot at save-time from `tempLayer` / `animationObjRef` when present
 *    (drag/resize often mutates refs in place without a new state array).
 * 3) Sync `animation[0]` x/y/size from `componentStyles` before PUT.
 *    On reload, hydration overlays first keyframe onto styles; without sync,
 *    moves look saved in Network but revert after refresh.
 * 4) In-flight lock (`isSavingRef`) prevents overlapping PUTs.
 * 5) Revision check after response: if the user edited during the request,
 *    keep dirty and reschedule; do not mark that newer work as saved.
 * 6) Session-expiry flag (`saveCanvasDataOnSessionExpire`) triggers one flush.
 *
 * WIRING
 * ------
 * Called from `EditorMainPage` after `persistBannerCanvas` is defined.
 * Tune timings via the constants below.
 * =============================================================================
 */

/** Wait after last edit before attempting save. */
const AUTO_SAVE_DEBOUNCE_MS = 4000;

/** Force a save during continuous editing (dirty streak cap). */
const AUTO_SAVE_MAX_WAIT_MS = 30000;

/** Pause after asset hydration before enabling auto-save. */
const AUTO_SAVE_SETTLE_MS = 800;

/**
 * Align first animation keyframe with current layer geometry.
 *
 * Load path (`useEditorAssetHydration`) restores position from
 * `animation[0].x/y` (not only `componentStyles.left/top`). Drag/resize
 * usually updates styles only — sync here so refresh shows latest layout.
 */
const syncAnimationWithLayerStyles = (layers, animationObject) => {
  const next = JSON.parse(JSON.stringify(animationObject || {}));

  (layers || []).forEach((layer) => {
    if (!layer?.id || !next[layer.id]?.animation?.length) return;

    const styles = layer.componentStyles || {};
    const frame0 = next[layer.id].animation[0];

    next[layer.id].animation[0] = {
      ...frame0,
      x: styles.left ?? frame0.x,
      y: styles.top ?? frame0.y,
      width: styles.width ?? frame0.width,
      height: styles.height ?? frame0.height,
    };
  });

  return next;
};

/**
 * @param {object} params
 * @param {Function} params.persistBannerCanvas - Slim canvas PUT helper from EditorMainPage
 * @param {boolean} params.bannerChanged - React dirty flag
 * @param {React.MutableRefObject<boolean>} params.refBannerChanged - Ref dirty flag (async-safe)
 * @param {Function} params.setBannerChanged
 * @param {Array} params.layerCompData - Canvas layers (React state)
 * @param {object} params.animationObject - Per-layer animation map (React state)
 * @param {string} params.bannerId - Route param fallback when assetInfo is not set yet
 * @param {number} params.refreshCompleted - Increments when asset hydration finishes
 * @param {React.MutableRefObject} params.tempLayer - Latest layers (incl. in-place drag mutations)
 * @param {React.MutableRefObject} params.animationObjRef - Latest animation map ref
 */
export const useEditorAutoSave = ({
  persistBannerCanvas,
  bannerChanged,
  refBannerChanged,
  setBannerChanged,
  layerCompData,
  animationObject,
  bannerId,
  refreshCompleted,
  tempLayer,
  animationObjRef,
}) => {
  const appData = useContext(ValueContext);

  // --- mutable control state (refs avoid re-creating timers on every render) ---
  const isSavingRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const maxWaitTimerRef = useRef(null);
  /** Monotonic counter: bumps whenever canvas React state changes. */
  const revisionRef = useRef(0);
  /** Revision that was last successfully persisted (or baselined at enable). */
  const lastSavedRevisionRef = useRef(null);
  /** False until hydration + settle; blocks all scheduling. */
  const enabledRef = useRef(false);
  const persistRef = useRef(persistBannerCanvas);
  const setBannerChangedRef = useRef(setBannerChanged);
  const bannerChangedRef = useRef(bannerChanged);
  const assetIdRef = useRef(null);
  /** Bumps when auto-save becomes enabled so the scheduler effect re-runs. */
  const [readyTick, setReadyTick] = useState(0);

  // Keep refs current for async timers / in-flight saves.
  persistRef.current = persistBannerCanvas;
  setBannerChangedRef.current = setBannerChanged;
  bannerChangedRef.current = bannerChanged;
  assetIdRef.current = appData?.assetInfo?.assetId || bannerId || null;

  // Track content revisions from React state updates.
  useEffect(() => {
    revisionRef.current += 1;
  }, [layerCompData, animationObject]);

  /**
   * Gate: enable only after the asset has been loaded (`refreshCompleted`).
   * Baseline `lastSavedRevision` to the current revision so the initial
   * hydration itself does not look like an unsaved edit.
   */
  useEffect(() => {
    const assetId = appData?.assetInfo?.assetId || bannerId;
    enabledRef.current = false;

    if (!assetId || !refreshCompleted) {
      lastSavedRevisionRef.current = null;
      return undefined;
    }

    const timer = setTimeout(() => {
      lastSavedRevisionRef.current = revisionRef.current;
      enabledRef.current = true;
      setReadyTick((tick) => tick + 1);
    }, AUTO_SAVE_SETTLE_MS);

    return () => clearTimeout(timer);
  }, [appData?.assetInfo?.assetId, bannerId, refreshCompleted]);

  /**
   * Scheduler: on pending changes, arm debounce (+ max-wait once per streak).
   * Cleanup only clears debounce so max-wait survives rapid edit churn.
   */
  useEffect(() => {
    const clearDebounce = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };

    const clearMaxWait = () => {
      if (maxWaitTimerRef.current) {
        clearTimeout(maxWaitTimerRef.current);
        maxWaitTimerRef.current = null;
      }
    };

    const hasPendingChanges = () => {
      if (!enabledRef.current || lastSavedRevisionRef.current === null) {
        return false;
      }
      const revisionChanged =
        revisionRef.current !== lastSavedRevisionRef.current;
      const flaggedDirty =
        bannerChangedRef.current === true ||
        refBannerChanged?.current === true;
      return revisionChanged || flaggedDirty;
    };

    /** Prefer live refs — they include in-place drag/resize mutations. */
    const getLatestCanvasSnapshot = () => {
      const layers =
        Array.isArray(tempLayer?.current) && tempLayer.current.length > 0
          ? tempLayer.current
          : layerCompData;
      const anim =
        animationObjRef?.current &&
        typeof animationObjRef.current === "object" &&
        !Array.isArray(animationObjRef.current)
          ? animationObjRef.current
          : animationObject;

      return {
        layerCompData: JSON.parse(JSON.stringify(layers || [])),
        animationObject: syncAnimationWithLayerStyles(layers, anim),
      };
    };

    const runAutoSave = async () => {
      const assetId = assetIdRef.current;
      if (!assetId || !hasPendingChanges() || isSavingRef.current) return;

      clearDebounce();
      clearMaxWait();

      const revisionAtStart = revisionRef.current;
      const snapshot = getLatestCanvasSnapshot();
      isSavingRef.current = true;

      try {
        const ok = await persistRef.current({
          origin: "autoSave",
          layerCompData: snapshot.layerCompData,
          animationObject: snapshot.animationObject,
        });

        if (ok) {
          if (revisionRef.current === revisionAtStart) {
            // No edits during the request — safe to clear dirty.
            lastSavedRevisionRef.current = revisionAtStart;
            if (refBannerChanged) refBannerChanged.current = false;
            setBannerChangedRef.current?.(false);
          } else if (refBannerChanged) {
            // Newer edits arrived while PUT was in flight — keep dirty.
            refBannerChanged.current = true;
            setBannerChangedRef.current?.(true);
          }
        }
      } finally {
        isSavingRef.current = false;
        if (hasPendingChanges()) {
          schedule();
        }
      }
    };

    const schedule = () => {
      if (!assetIdRef.current || !hasPendingChanges() || isSavingRef.current) {
        return;
      }

      clearDebounce();
      debounceTimerRef.current = setTimeout(runAutoSave, AUTO_SAVE_DEBOUNCE_MS);

      // Arm max-wait only once until a save runs (or dirty clears).
      if (!maxWaitTimerRef.current) {
        maxWaitTimerRef.current = setTimeout(
          runAutoSave,
          AUTO_SAVE_MAX_WAIT_MS
        );
      }
    };

    if (!assetIdRef.current || !hasPendingChanges()) {
      clearDebounce();
      clearMaxWait();
      return clearDebounce;
    }

    schedule();
    return clearDebounce;
  }, [
    appData?.assetInfo?.assetId,
    bannerId,
    layerCompData,
    animationObject,
    refreshCompleted,
    readyTick,
    bannerChanged,
    refBannerChanged,
    tempLayer,
    animationObjRef,
  ]);

  // Unmount: clear both timers.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (maxWaitTimerRef.current) clearTimeout(maxWaitTimerRef.current);
    };
  }, []);

  /**
   * Session expiry flush — `SessionExpiryManager` sets
   * `saveCanvasDataOnSessionExpire`; we persist once then clear the flag.
   */
  useEffect(() => {
    if (!appData?.saveCanvasDataOnSessionExpire) return;

    const assetId = appData?.assetInfo?.assetId || bannerId;
    if (!assetId) {
      appData?.setSaveCanvasDataOnSessionExpire?.(false);
      return;
    }

    const flush = async () => {
      if (isSavingRef.current) return;
      isSavingRef.current = true;
      try {
        const layers =
          Array.isArray(tempLayer?.current) && tempLayer.current.length > 0
            ? tempLayer.current
            : layerCompData;
        const anim =
          animationObjRef?.current &&
          typeof animationObjRef.current === "object"
            ? animationObjRef.current
            : animationObject;

        const ok = await persistRef.current({
          origin: "autoSave",
          layerCompData: JSON.parse(JSON.stringify(layers || [])),
          animationObject: syncAnimationWithLayerStyles(layers, anim),
        });
        if (ok) {
          lastSavedRevisionRef.current = revisionRef.current;
        }
      } finally {
        isSavingRef.current = false;
        appData?.setSaveCanvasDataOnSessionExpire?.(false);
      }
    };

    flush();
  }, [appData?.saveCanvasDataOnSessionExpire, bannerId]);
};
